import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Snackbar,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Card,
    CardContent,
    List
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Sync as SyncIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PlayArrow as ParseIcon,
    History as HistoryIcon,
    Description as FileIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    Article as DocIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Refresh as ReassessIcon,
} from '@mui/icons-material';
import {
    FileType,
    FileSource,
    WorkerFile,
    FileParseHistory,
    getWorkerFiles,
    updateWorkerFile,
    deleteWorkerFile,
    downloadWorkerFile,
    parseWorkerFileForReview,
    getFileParseHistory,
    FILE_TYPE_LABELS,
} from '../../api/workerFilesApi';
import { uploadWorkerFileToEasyFlex } from '../../api/workersApi';
import { ingestCertFromWorkerFile, fetchWorkerCertifications } from '../../api/workerCertificationsApi';
import { CertReviewStatus } from '../../models/worker';
import { format } from 'date-fns';
import socketService from '../../services/socketService';
import logger from '../../utils/logger';
import { FilePreviewModal } from '../WorkerFiles/FilePreviewModal';
import { FileParseResultsModal, FileParseResultsModalRef } from './FileParseResultsModal';
import FileUploadModal from './FileUploadModal';
import { fetchSessionQuestionsForWorker, SessionQuestionEntityFrontend } from '../../api/sessionQuestionsApi';

interface WorkerFileManagerProps {
    workerId: string;
    /** Numeric worker PK (worker.id), needed to look up this worker's
     *  certifications so the Files list can tell which certificate files already
     *  have a VERIFIED cert and hide the "Send to AI" action for them. */
    workerNumericId?: number;
    onCVParsed?: () => void;
    /** Bumping this value forces a file-list refetch. Parent uses it after
     *  generating a NAW PDF so the new FileType.NAW_DOCUMENT row shows up in
     *  the NAW Docs tab without a page reload. */
    refreshKey?: number;
}

const FILE_TYPES_FOR_SELECTION: FileType[] = [
    FileType.CV,
    FileType.CERTIFICATION,
    FileType.RIJBEWIJS,
    FileType.ID_DOCUMENT,
    // VCA gets its own slot so the recruiter can deliberately upload a VCA
    // cert (which routes through the dedicated /documents/extract-vca prompt)
    // instead of the generic CERTIFICATION pipeline.
    FileType.VCA,
    FileType.REFERENCE,
    FileType.OTHER,
];

const getReadableFileType = (fileType: FileType): string => FILE_TYPE_LABELS[fileType] ?? fileType;

/**
 * IdentificationDocuments writes structured metadata into the `description`
 * column as a `worker-doc-v1` JSON blob (rijbewijs side/number, ID type, etc.).
 * Render something readable for those rows instead of dumping the JSON.
 */
const prettifyFileDescription = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{')) return raw;
    try {
        const meta = JSON.parse(trimmed);
        if (meta && meta.schema === 'worker-doc-v1') {
            const title = typeof meta.title === 'string' ? meta.title.trim() : '';
            const issuer = typeof meta.issuer === 'string' ? meta.issuer.trim() : '';
            const docType = typeof meta.docType === 'string' ? meta.docType : '';
            if (title && issuer) return `${title} · ${issuer}`;
            if (title) return title;
            if (issuer) return issuer;
            if (docType === 'CERTIFICATION') return 'Certificate';
            if (docType === 'RIJBEWIJS') return 'Driver\'s licence';
            if (docType === 'ID_DOCUMENT') return 'ID document';
            return docType || 'Document';
        }
    } catch {
        // Not JSON we recognise — fall through and show raw.
    }
    return raw;
};

const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') return <PdfIcon />;
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType.includes('document') || mimeType.includes('msword') || mimeType.includes('openxmlformats')) return <DocIcon />;
    return <FileIcon />;
};

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const WorkerFileManager: React.FC<WorkerFileManagerProps> = ({ workerId, workerNumericId, onCVParsed, refreshKey }) => {
    const [files, setFiles] = useState<WorkerFile[]>([]);
    // worker_file ids that already have a VERIFIED certificate — their "Send to
    // AI" reassess action is hidden (the cert is done, re-extracting is pointless).
    const [verifiedCertFileIds, setVerifiedCertFileIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<WorkerFile | null>(null);
    const [parseHistory, setParseHistory] = useState<FileParseHistory[]>([]);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'info'
    });

    // Edit state
    const [editFileType, setEditFileType] = useState<FileType>(FileType.CV);
    const [editDescription, setEditDescription] = useState('');

    // Parse results state
    const [parseResultsModalOpen, setParseResultsModalOpen] = useState(false);
    const [parsingFile, setParsingFile] = useState<WorkerFile | null>(null);
    const [parsingFileId, setParsingFileId] = useState<number | null>(null);
    const [uploadingToEasyFlexFileId, setUploadingToEasyFlexFileId] = useState<number | null>(null);
    const [reassessingFileId, setReassessingFileId] = useState<number | null>(null);
    const [questions, setQuestions] = useState<SessionQuestionEntityFrontend[]>([]);
    const [pendingParseResults, setPendingParseResults] = useState<any>(null);
    const parseResultsModalRef = useRef<FileParseResultsModalRef>(null);

    useEffect(() => {
        fetchFiles();
        fetchQuestions();

        // Set up socket listeners for parse results
        const socket = socketService.connect();
        
        // Join the session room to receive parse results
        socket.emit('joinSessionRoom', workerId);
        
        const handleSocketParseResults = (results: any) => {
            logger.info('Received file parse results from socket:', results);
            logger.info('Results structure: ' + JSON.stringify({
                hasWorkerId: 'workerId' in results,
                hasJobId: 'jobId' in results,
                currentWorkerId: workerId
            }));
            
            // The results might not have workerId if it's from file parsing
            // Check if we're currently waiting for parse results
            if (parseResultsModalRef.current && results.jobId) {
                logger.info('Passing results to modal directly');
                // Add workerId to results if not present
                const resultsWithWorkerId = {
                    ...results,
                    workerId: results.workerId || workerId
                };
                parseResultsModalRef.current.handleParseResults(resultsWithWorkerId);
                setPendingParseResults(null); // Clear pending results
                fetchFiles(); // Refresh to show updated parse status
            } else {
                logger.warn('Modal ref not available or no jobId in results:', {
                    hasModalRef: !!parseResultsModalRef.current,
                    hasJobId: !!results.jobId,
                    results: results
                });
                // Store results for when modal becomes available
                if (results.jobId) {
                    setPendingParseResults(results);
                }
            }
        };
        
        socket.on('fileParsingResults', handleSocketParseResults);

        return () => {
            socket.emit('leaveSessionRoom', workerId);
            socket.off('fileParsingResults', handleSocketParseResults);
        };
    }, [workerId]);

    // External refresh trigger: parent bumps `refreshKey` after a side-channel
    // write (e.g. NawDocumentSection generated a NAW PDF) so the new
    // FileType.NAW_DOCUMENT row appears in the NAW Docs tab without a page
    // reload. Skip the initial render (refreshKey==null on first mount) —
    // the workerId effect above already did the first fetch.
    useEffect(() => {
        if (refreshKey == null) return;
        fetchFiles();
        // fetchFiles is a stable closure over workerId; no need to list it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // Load which certificate files already have a VERIFIED cert so their
    // "Send to AI" action can be hidden.
    const loadVerifiedCertFileIds = useCallback(async () => {
        if (workerNumericId == null) {
            setVerifiedCertFileIds(new Set());
            return;
        }
        try {
            const certs = await fetchWorkerCertifications(workerNumericId);
            const verified = new Set<number>();
            for (const c of certs) {
                if (c.status === CertReviewStatus.VERIFIED && c.workerFileId != null) {
                    verified.add(c.workerFileId);
                }
            }
            setVerifiedCertFileIds(verified);
        } catch {
            // Non-fatal: worst case the reassess button stays visible.
        }
    }, [workerNumericId]);

    useEffect(() => { loadVerifiedCertFileIds(); }, [loadVerifiedCertFileIds, refreshKey]);

    // Live-refresh the verified set when a cert is confirmed/rejected in the
    // review tab (or the global queue) — the recruiter approves in one place and
    // the "Send to AI" button here flips to "Verified" without a page reload.
    useEffect(() => {
        const onReviewed = (payload: { workerId: number }) => {
            if (payload.workerId === workerNumericId) loadVerifiedCertFileIds();
        };
        socketService.onCertReviewUpdated(onReviewed);
        return () => { socketService.offCertReviewUpdated(onReviewed); };
    }, [workerNumericId, loadVerifiedCertFileIds]);

    // Handle pending parse results when modal becomes available
    useEffect(() => {
        if (parseResultsModalRef.current && pendingParseResults) {
            logger.info('Modal now available, processing pending results');
            const resultsWithWorkerId = {
                ...pendingParseResults,
                workerId: pendingParseResults.workerId || workerId
            };
            parseResultsModalRef.current.handleParseResults(resultsWithWorkerId);
            setPendingParseResults(null);
            fetchFiles(); // Refresh to show updated parse status
        }
    }, [pendingParseResults, workerId]);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            const fetchedFiles = await getWorkerFiles(workerId);
            setFiles(fetchedFiles);
        } catch (error: any) {
            showSnackbar('Failed to load files', 'error');
            logger.error('Failed to fetch worker files:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuestions = async () => {
        try {
            const data = await fetchSessionQuestionsForWorker(workerId);
            setQuestions(data);
        } catch (error: any) {
            logger.error('Failed to fetch questions:', error);
        }
    };


    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbar({ open: true, message, severity: severity === 'warning' ? 'info' : severity });
    };

    const handleParseSaveComplete = (updatedQuestions: SessionQuestionEntityFrontend[]) => {
        setQuestions(updatedQuestions);
        if (onCVParsed) {
            onCVParsed();
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleReassessCert = async (file: WorkerFile) => {
        setReassessingFileId(file.id);
        try {
            const { jobId, dedupedCertIds } = await ingestCertFromWorkerFile(file.id);
            if (jobId === null) {
                // v7 dedup hit — byte-identical file already ingested for this
                // worker. Rows already in the review queue.
                showSnackbar(
                    `Already in the review queue (${dedupedCertIds.length} cert(s)). Open Certificate Review to manage it.`,
                    'info',
                );
            } else {
                showSnackbar(
                    'Queued for AI extraction. The cert(s) will appear in the review queue shortly.',
                    'success',
                );
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message ?? error?.message ?? 'Reassess failed';
            showSnackbar(`Reassess failed: ${msg}`, 'error');
        } finally {
            setReassessingFileId(null);
        }
    };

    const handleEdit = (file: WorkerFile) => {
        setSelectedFile(file);
        setEditFileType(file.fileType);
        setEditDescription(file.description || '');
        setEditDialogOpen(true);
    };

    const handleUpdate = async () => {
        if (!selectedFile) return;

        try {
            await updateWorkerFile(selectedFile.id, {
                fileType: editFileType,
                description: editDescription,
            });
            showSnackbar('File updated successfully', 'success');
            setEditDialogOpen(false);
            fetchFiles();
        } catch (error: any) {
            showSnackbar('Failed to update file', 'error');
        }
    };

    const handleUploadToEasyFlex = async (file: WorkerFile) => {
        setUploadingToEasyFlexFileId(file.id);
        try {
            await uploadWorkerFileToEasyFlex(workerId, file.id);
            showSnackbar('Uploaded to EasyFlex', 'success');
        } catch (error: any) {
            showSnackbar(error?.message || 'Failed to upload to EasyFlex', 'error');
        } finally {
            setUploadingToEasyFlexFileId(null);
        }
    };

    const handleDelete = async () => {
        if (!selectedFile) return;

        try {
            await deleteWorkerFile(selectedFile.id);
            showSnackbar('File deleted successfully', 'success');
            setDeleteDialogOpen(false);
            fetchFiles();
        } catch (error: any) {
            showSnackbar('Failed to delete file', 'error');
        }
    };

    const handleDownload = async (file: WorkerFile) => {
        try {
            const blob = await downloadWorkerFile(file.id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.originalFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            showSnackbar('Failed to download file', 'error');
        }
    };

    const handlePreview = (file: WorkerFile) => {
        if (file.mimeType === 'application/pdf' || file.mimeType.startsWith('image/')) {
            setSelectedFile(file);
            setPreviewDialogOpen(true);
        } else {
            handleDownload(file);
        }
    };

    const handleParse = async (file: WorkerFile) => {
        try {
            setParsingFileId(file.id);
            setParsingFile(file);
            setParseResultsModalOpen(true);
            
            const result = await parseWorkerFileForReview(file.id);
            showSnackbar(result.message, 'info');
            
            // The WebSocket will deliver the results to handleParseResults
        } catch (error: any) {
            showSnackbar('Failed to parse file', 'error');
            setParsingFileId(null);
            setParseResultsModalOpen(false);
        }
    };

    const handleViewHistory = async (file: WorkerFile) => {
        setSelectedFile(file);
        try {
            const history = await getFileParseHistory(file.id);
            setParseHistory(history);
            setHistoryDialogOpen(true);
        } catch (error: any) {
            showSnackbar('Failed to load parse history', 'error');
        }
    };

    // Tabs in order: All / CV / Certifications / VCA / ID / NAW / Other
    //
    // "Other" intentionally catches every FileType not pulled out into its own
    // tab — RIJBEWIJS, PROFILE_PICTURE, WORK_PICTURE, and the literal OTHER
    // bucket. Adding a new FileType automatically lands it there until
    // someone promotes it to its own tab. Without this, a new type silently
    // appears only under "All Files" (live-test finding).
    //
    // NAW_DOCUMENT gets its own tab so recruiter-generated NAW PDFs are
    // immediately discoverable after clicking Generate in NawDocumentSection.
    const OWN_TAB_TYPES = new Set<FileType>([
        FileType.CV,
        FileType.CERTIFICATION,
        FileType.VCA,
        FileType.ID_DOCUMENT,
        FileType.NAW_DOCUMENT,
    ]);

    const getFilteredFiles = () => {
        switch (tabValue) {
            case 1:
                return files.filter(f => f.fileType === FileType.CV);
            case 2:
                return files.filter(f => f.fileType === FileType.CERTIFICATION);
            case 3:
                return files.filter(f => f.fileType === FileType.VCA);
            case 4:
                return files.filter(f => f.fileType === FileType.ID_DOCUMENT);
            case 5:
                return files.filter(f => f.fileType === FileType.NAW_DOCUMENT);
            case 6:
                return files.filter(f => !OWN_TAB_TYPES.has(f.fileType));
            case 0:
            default:
                return files;
        }
    };

    const filteredFiles = getFilteredFiles();

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Worker Files</Typography>
                <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                >
                    Upload File
                </Button>
            </Box>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="file type tabs" variant="scrollable" scrollButtons="auto">
                    <Tab label={`All Files (${files.length})`} />
                    <Tab label={`CVs (${files.filter(f => f.fileType === FileType.CV).length})`} />
                    <Tab label={`Certificates (${files.filter(f => f.fileType === FileType.CERTIFICATION).length})`} />
                    <Tab label={`VCA (${files.filter(f => f.fileType === FileType.VCA).length})`} />
                    <Tab label={`ID Documents (${files.filter(f => f.fileType === FileType.ID_DOCUMENT).length})`} />
                    <Tab label={`NAW Docs (${files.filter(f => f.fileType === FileType.NAW_DOCUMENT).length})`} />
                    <Tab label={`Other (${files.filter(f => !OWN_TAB_TYPES.has(f.fileType)).length})`} />
                </Tabs>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>File</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Uploaded</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFiles.map((file) => (
                                <TableRow key={file.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getFileIcon(file.mimeType)}
                                            <Box>
                                                <Typography variant="body2">{file.originalFileName}</Typography>
                                                {file.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {prettifyFileDescription(file.description)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getReadableFileType(file.fileType)}
                                            size="small"
                                            color={file.fileType === FileType.CV ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{formatFileSize(file.sizeBytes)}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {format(new Date(file.createdAt), 'MMM dd, yyyy')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                by {file.uploadedByName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {file.fileType === FileType.CV && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {file.source === FileSource.GENERATED ? (
                                                    <Chip
                                                        label="AI-generated"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ) : file.isParsed ? (
                                                    <Chip
                                                        icon={<CheckIcon />}
                                                        label={`Parsed (${file.parseCount}x)`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ) : parsingFileId === file.id ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Chip
                                                        label="Not Parsed"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <Tooltip title="Preview/Download">
                                                <IconButton size="small" onClick={() => handlePreview(file)}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Upload to EasyFlex">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleUploadToEasyFlex(file)}
                                                        disabled={uploadingToEasyFlexFileId === file.id}
                                                    >
                                                        {uploadingToEasyFlexFileId === file.id ? (
                                                            <CircularProgress size={18} />
                                                        ) : (
                                                            <SyncIcon />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {file.fileType === FileType.CV && (
                                                <>
                                                    <Tooltip title={file.source === FileSource.GENERATED
                                                        ? 'This CV was generated by the app — there is nothing to parse'
                                                        : 'Parse CV'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleParse(file)}
                                                                disabled={parsingFileId === file.id || file.source === FileSource.GENERATED}
                                                            >
                                                                <ParseIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    {file.isParsed && (
                                                        <Tooltip title="View Parse History">
                                                            <IconButton size="small" onClick={() => handleViewHistory(file)}>
                                                                <HistoryIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {file.fileType === FileType.CERTIFICATION && (
                                                verifiedCertFileIds.has(file.id) ? (
                                                    <Tooltip title="This certificate has already been reviewed and verified">
                                                        <Chip
                                                            icon={<CheckIcon />}
                                                            label="Verified"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Send to AI for certificate extraction">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleReassessCert(file)}
                                                                disabled={reassessingFileId === file.id}
                                                            >
                                                                {reassessingFileId === file.id ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <ReassessIcon />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )
                                            )}
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => handleEdit(file)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => {
                                                        setSelectedFile(file);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <FileUploadModal
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                workerId={workerId}
                onSuccess={() => {
                    setUploadDialogOpen(false);
                    fetchFiles();
                }}
            />

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit File</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>File Type</InputLabel>
                            <Select
                                value={editFileType}
                                onChange={(e) => setEditFileType(e.target.value as FileType)}
                                label="File Type"
                            >
                                {FILE_TYPES_FOR_SELECTION.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {getReadableFileType(type)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={2}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdate} variant="contained">Update</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete File</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedFile?.originalFileName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>


            {/* Parse History Dialog */}
            <Dialog 
                open={historyDialogOpen} 
                onClose={() => setHistoryDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>Parse History - {selectedFile?.originalFileName}</DialogTitle>
                <DialogContent>
                    <List>
                        {parseHistory.map((history) => (
                            <Card key={history.id} sx={{ mb: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Chip
                                            icon={
                                                history.status === 'SUCCESS' ? <CheckIcon /> :
                                                history.status === 'PARTIAL' ? <WarningIcon /> :
                                                <ErrorIcon />
                                            }
                                            label={history.status}
                                            color={
                                                history.status === 'SUCCESS' ? 'success' :
                                                history.status === 'PARTIAL' ? 'warning' :
                                                'error'
                                            }
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {format(new Date(history.createdAt), 'MMM dd, yyyy HH:mm')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                        Extracted {history.questionsExtracted} of {history.totalQuestions} questions
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        by {history.parsedByName} • {history.duration}ms
                                    </Typography>
                                    {history.errorMessage && (
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                            {history.errorMessage}
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* File Preview Modal */}
            {selectedFile && (
                <FilePreviewModal
                    open={previewDialogOpen}
                    onClose={() => setPreviewDialogOpen(false)}
                    file={selectedFile}
                />
            )}

            {/* File Parse Results Modal */}
            <FileParseResultsModal
                ref={parseResultsModalRef}
                open={parseResultsModalOpen}
                onClose={() => {
                    setParseResultsModalOpen(false);
                    setParsingFile(null);
                    setParsingFileId(null);
                }}
                file={parsingFile}
                workerId={workerId}
                questions={questions}
                onSaveComplete={handleParseSaveComplete}
                showSnackbar={showSnackbar}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ width: '100%', boxShadow: 3 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};
